import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { validateOrigin, validateUpstream } from '@kentik/ui-shared/synthetics/bgp';
import { Flex, Tooltip } from 'core/components';
import { Form, Field, Select } from 'core/form/components';
import { dateRangeDisplay } from 'core/util/dateUtils';
import VizWrapper from '../utils/VizWrapper';
import { CROSSHATCH_PATH, CROSSHATCH_SIZE } from '../utils/constants';
import Timeline from './timeline';

const fields = {
  hop: {
    label: 'Show reachability by',
    options: [
      {
        label: 'Origin',
        value: 'origin_asn'
      },
      {
        label: 'Upstream',
        value: 'upstream_asn'
      },
      {
        label: 'Second Upstream',
        value: 'second_upstream_asn'
      }
    ],
    defaultValue: 'origin_asn',
    type: 'select'
  }
};

/**
 * Classify a reachability series as expected or unexpected.
 *
 * For origin_asn: validates against configured origin ASN list (RPKI unavailable in
 * reachability data, so check_rpki is not used).
 * For upstream_asn: validates against configured upstream list + TFZ.
 * For second_upstream_asn / prefix: no classification — always expected. Second upstreams
 * lack the full AS path context needed for meaningful leak detection; the PathGraph view
 * handles that via detectUpstreamLeak().
 */
const classifySeries = ({ groupBy, dimension, config }) => {
  if (!config || !dimension) {
    return true;
  }

  if (groupBy === 'origin_asn') {
    // Skip RPKI: pass check_rpki=false since reachability data has no rpki_status
    const { invalid } = validateOrigin({ ...config, check_rpki: false }, { origin_asn: parseInt(dimension, 10) });
    return !invalid;
  }

  if (groupBy === 'upstream_asn') {
    const { invalid } = validateUpstream(config, {
      upstream_asn: parseInt(dimension, 10)
    });
    return !invalid;
  }

  // second_upstream_asn and prefix grouping — no meaningful validation without full path context
  return true;
};

@inject('$bgp', '$colors')
@Form({ fields, options: { name: 'Bgp Test Results Reachability Graph Options' } })
@observer
class ReachabilityTimelineWrapper extends Component {
  // The bucket width of the reachability data, driven by the backend's chosen aggregation
  // interval for the lookback period. Falls back to 1 minute before data has loaded.
  getDelta = () => {
    const { $bgp } = this.props;
    const stepSeconds = $bgp.reachabilityTimeseriesData?.step_seconds;

    return stepSeconds ? stepSeconds * 1000 : 60000;
  };

  onClickTimeline = (startDate) => {
    const { onClickTimeline } = this.props;
    const delta = this.getDelta();

    if (onClickTimeline) {
      onClickTimeline(startDate, startDate + delta);
    }
  };

  getTimelineTitle = () => {
    const { startDate, endDate } = this.props;
    const delta = this.getDelta();
    const dateRangeText = dateRangeDisplay({ starting_time: startDate * 1000, ending_time: endDate * 1000 });

    return `${dateRangeText} (${delta / 1000 / 60} minute increments)`;
  };

  renderGraphForm() {
    const { prefixes } = this.props;

    // Disable hop select when showing multiple prefixes
    const multiplePrefixes = prefixes && prefixes.length > 1;

    return (
      <Flex>
        <Tooltip content="Select a single prefix to enable this option" disabled={!multiplePrefixes}>
          <Field name="hop" large mb={0} disabled={multiplePrefixes}>
            <Select />
          </Field>
        </Tooltip>
      </Flex>
    );
  }

  parseData = ({ data, prefixes, groupBy }) => {
    if (!data || !data.timeseries || !data.timestamps) {
      return { data: [] };
    }
    const { $colors, $bgp, model } = this.props;
    const colors = $colors.qualitativeColors;
    const config = model?.get('config.bgp') || {};

    const { timeseries, timestamps } = data;
    const series = [];

    // Create a Set of prefixes we care about for faster lookup
    const prefixSet = new Set(prefixes);

    let seriesIndex = 0;

    // Transform the new data structure into the format expected by the timeline
    // Only process prefixes that are in our prefixes array
    Object.entries(timeseries).forEach(([prefix, prefixData]) => {
      // Filter to only the prefixes we care about
      if (!prefixSet.has(prefix)) {
        return;
      }

      const seriesForHopType = prefixData[groupBy] || [];

      seriesForHopType.forEach((groupedSeries) => {
        const { dimension, name, values } = groupedSeries;
        // Convert to timeline data format: array of {x: timestamp_ms, y: percentage}
        const seriesData = timestamps.map((timestamp, idx) => {
          const time = timestamp * 1000; // Convert to milliseconds
          return {
            time,
            x: time,
            y: values[idx] || 0
          };
        });
        const seriesColor = $bgp.asnColorMap.get(dimension) ?? colors[seriesIndex % colors.length];

        // Classify expected/unexpected based on groupBy and test config.
        // Note: RPKI status is not available in the reachability API response,
        // so origin validation only checks against the configured origin ASN list.
        const expected = classifySeries({ groupBy, dimension, config });

        series.push({
          index: seriesIndex,
          name,
          data: seriesData,
          marker: {
            symbol: 'circle'
          },
          // The shared color map only contains ASN IDs. For prefix-level grouping,
          // dimension is a prefix string and the fallback palette color will apply.
          color: expected
            ? seriesColor
            : {
                pattern: {
                  path: {
                    d: CROSSHATCH_PATH,
                    strokeWidth: 2,
                    stroke: 'rgba(255, 255, 255, 0.6)'
                  },
                  width: CROSSHATCH_SIZE,
                  height: CROSSHATCH_SIZE,
                  backgroundColor: seriesColor,
                  opacity: 0.75
                }
              },
          fillOpacity: 0.7,
          prefix,
          dimension,
          expected,
          // Unexpected series in line mode use dashed lines
          dashStyle: expected ? 'Solid' : 'ShortDash'
        });
        seriesIndex += 1;
      });
    });

    let type;
    let stacking;

    if (prefixes.length === 1) {
      type = 'area';
      stacking = 'normal';
    } else {
      type = 'line';
      stacking = undefined;
    }

    return {
      data: series,
      graphProps: {
        type,
        stacking
      }
    };
  };

  render() {
    const { $bgp, form, onHover, xAxisMin, xAxisMax, prefixes } = this.props;
    const loading = $bgp.reachabilityTimeseriesLoading;
    const data = $bgp.reachabilityTimeseriesData;

    // If multiple prefixes are selected, we always use 'prefix' groupBy to show combined data
    const groupBy = prefixes.length > 1 ? 'prefix' : form.getValue('hop');

    let results = [];
    let graphProps = {};
    let hasResults = false;
    if (data) {
      const parsed = this.parseData({ data, prefixes, groupBy });
      results = parsed.data;
      graphProps = parsed.graphProps;
      hasResults = !!results.length;

      // TODO: render VP count somewhere. Show version based on which prefixes are selected
      // const hasIpv6Prefix = prefixes.some((p) => p.includes(':'));
    }

    return (
      <VizWrapper
        key="bgp-reachability-timeline"
        loading={loading}
        renderContent={hasResults}
        heading="Reachability / Visibility"
        subtitle="Percentage of Kentik's BGP vantage points (VPs) with routes to the monitored prefixes."
        noResultsDescription="No monitored prefixes were reachable from any Vantage Points during this time range"
        menu={this.renderGraphForm()}
      >
        <Timeline
          data={results}
          onHover={onHover}
          onClick={this.onClickTimeline}
          xAxisMin={xAxisMin}
          xAxisMax={xAxisMax}
          xAxisTitle={this.getTimelineTitle()}
          {...graphProps}
        />
      </VizWrapper>
    );
  }
}

export default ReachabilityTimelineWrapper;
